import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError } from "axios";
import { useState } from "react";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
// import { toast } from 'react-toastify';
import * as Yup from "yup";

import HttpService from "../../app/http";
import BsInputPassword from "../../components/common/BsInputPassword";
import { AlertsType } from "../../constants/alerts.constant";
import useAppLocalStorage from "../../hooks/useAppLocalStorage";
import useAuth from "../../hooks/useAuth";

const LevelUpPassword = () => {
  const { t } = useTranslation();
  const schema = Yup.object({
    actual_password: Yup.string().required().label("Current password"),
    new_password: Yup.string().required().label("New password"),
    new_confirm_password: Yup.string().oneOf(
      [Yup.ref("new_password")],
      t(AlertsType.ISATKO011)
    )
  });
  const localStorage = useAppLocalStorage();
  const { customer_identification } = useAuth();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [apiErrors, setApiErrors] = useState<any>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError
  } = useForm({
    resolver: yupResolver(schema)
  });

  const LevelUpPassword = {
    title: t("authPages/levelUpPassword/title"),
    resetBtn: t("authPages/levelUpPassword/btn-01"),
    brand: {
      title: t("authPages/levelUpPassword/t-01"),
      description: t("authPages/levelUpPassword/text-01")
    }
  };

  const PasswordCheckMutation = useMutation<
    any,
    AxiosError<any, any>,
    { password: string; level?: string | null }
  >((data) => HttpService.post("/auth/check-password", data), {
    onError: (err) => {
      // toast.error(err.response?.data.message || err.message)

      setApiErrors({ ...apiErrors, new_password: err.response?.data?.message });
      setError("new_password", err.response?.data.message);
    }
  });

  const UpdatePasswordMutation = useMutation<any, AxiosError<any, any>, any>(
    (data) =>
      HttpService.patch(
        `/${customer_identification}/profile/password`,
        {
          actual_password: data.actual_password,
          new_password: data.new_password
        },
        {
          auth: HttpService.getToken()
        }
      ),
    {
      onSuccess: () => {
        // toast.success(t(AlertsType.ISATKO015));
        reset();

        logoutDirectlyToLogin();
      },
      onError: (err) => {
        // toast.error(err.response?.data.message || err.message)

        if (err.response?.data?.message === "Credential error.") {
          setApiErrors({
            ...apiErrors,
            actual_password: err.response?.data?.message
          });
        } else {
          setApiErrors({
            ...apiErrors,
            new_password: err.response?.data?.message
          });
        }
      },
      onMutate: () => {
        setApiErrors({});
      }
    }
  );

  const onSubmit = (values: any) => {
    PasswordCheckMutation.mutate(
      {
        password: values.new_password,
        level: localStorage.getItem(
          `${import.meta.env.VITE_STORAGE_PREFIX}_password_level`
        )
      },
      {
        onSuccess: () => {
          UpdatePasswordMutation.mutate(values);
        }
      }
    );
  };

  const logoutDirectlyToLogin = () => {
    localStorage.removeItem(`${import.meta.env.VITE_STORAGE_PREFIX}_service`);
    auth.logout();
    queryClient.clear();
    navigate("login");
  };

  const formChangeHandler = () => {
    setApiErrors({});
  };

  return (
    <div className="min-vh-100 d-flex flex-column align-items-center justify-content-center">
      <Container>
        <Row className="justify-content-center">
          <Col lg={4} md={10} sm={10} xs={12}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h1 className="fs-4 fw-bold mb-0">{LevelUpPassword.title}</h1>
            </div>

            <Form
              onSubmit={handleSubmit(onSubmit)}
              onChange={formChangeHandler}
            >
              <BsInputPassword
                label="Current password"
                isInvalid={
                  !!errors.actual_password || !!apiErrors.actual_password
                }
                errorMessage={
                  errors.actual_password?.message || apiErrors.actual_password
                }
                className={"mb-3"}
                rhfControls={register("actual_password")}
              />

              <BsInputPassword
                label="New password"
                isInvalid={!!errors.new_password || !!apiErrors.new_password}
                errorMessage={
                  errors.new_password?.message || apiErrors.new_password
                }
                className={"mb-3"}
                rhfControls={register("new_password")}
              />

              <BsInputPassword
                label="Re-enter Password"
                isInvalid={!!errors.new_confirm_password}
                errorMessage={errors.new_confirm_password?.message as string}
                className={"mb-3"}
                rhfControls={register("new_confirm_password")}
              />

              <Button
                variant="primary"
                type="submit"
                className="w-100 rounded-pill auth-btn"
                size="lg"
                disabled={UpdatePasswordMutation.isLoading}
              >
                {UpdatePasswordMutation.isLoading ? (
                  <Spinner animation="grow" />
                ) : (
                  LevelUpPassword.resetBtn
                )}
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LevelUpPassword;
